import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './AdminRangeFilter.module.css';
import sharedStyles from '../AdminFilterShared/AdminFilterShared.module.css';
import filterIcon from '@/assets/icons/filter.svg';

export type RangeValue = {
  min: number;
  max: number;
};

type AdminRangeFilterProps = {
  label: string;
  bounds: RangeValue;
  value: RangeValue;
  histogramValues: number[];
  histogramBars?: number[];
  onChange: (next: RangeValue) => void;
  isActive?: boolean;
  bins?: number;
};

export const AdminRangeFilter = ({
  label,
  bounds,
  value,
  histogramValues,
  histogramBars: externalHistogramBars,
  onChange,
  isActive: isActiveProp,
  bins = 14,
}: AdminRangeFilterProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0, width: 248 });
  const filterRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);

  const isTriggerVisuallyHidden = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) {
      return true;
    }

    const rect = trigger.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) {
      return true;
    }

    if (
      rect.bottom <= 0 ||
      rect.right <= 0 ||
      rect.top >= window.innerHeight ||
      rect.left >= window.innerWidth
    ) {
      return true;
    }

    const points: Array<{ x: number; y: number }> = [
      { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 },
      { x: rect.left + 2, y: rect.top + rect.height / 2 },
      { x: rect.right - 2, y: rect.top + rect.height / 2 },
    ];

    return !points.some(({ x, y }) => {
      const element = document.elementFromPoint(x, y);
      return Boolean(element && trigger.contains(element));
    });
  }, []);

  const updatePopoverPosition = useCallback(() => {
    if (!triggerRef.current) {
      return;
    }

    const rect = triggerRef.current.getBoundingClientRect();
    const width = 248;
    const offsetTop = 10;
    const viewportPadding = 12;
    const left = Math.min(
      Math.max(rect.left - 100, viewportPadding),
      window.innerWidth - width - viewportPadding
    );

    setPopoverPosition({
      top: rect.bottom + offsetTop,
      left,
      width,
    });
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      const targetNode = event.target as Node;
      if (filterRef.current?.contains(targetNode) || popoverRef.current?.contains(targetNode)) {
        return;
      }
      setIsOpen(false);
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    const handleViewportChange = () => {
      updatePopoverPosition();
      if (isTriggerVisuallyHidden()) {
        setIsOpen(false);
      }
    };

    handleViewportChange();

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('scroll', handleViewportChange, true);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('scroll', handleViewportChange, true);
    };
  }, [isOpen, isTriggerVisuallyHidden, updatePopoverPosition]);

  useEffect(() => {
    if (!isOpen || !triggerRef.current) {
      return;
    }

    if (typeof IntersectionObserver === 'undefined') {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) {
          setIsOpen(false);
        }
      },
      {
        threshold: 0.01,
      }
    );

    observer.observe(triggerRef.current);

    return () => {
      observer.disconnect();
    };
  }, [isOpen]);

  const normalized = useMemo(() => {
    const min = Math.min(Math.max(value.min, bounds.min), bounds.max);
    const max = Math.max(min, Math.min(value.max, bounds.max));
    return { min, max };
  }, [bounds.max, bounds.min, value.max, value.min]);

  const span = Math.max(1, bounds.max - bounds.min);
  const minPercent = ((normalized.min - bounds.min) / span) * 100;
  const maxPercent = ((normalized.max - bounds.min) / span) * 100;
  const isActive = isActiveProp ?? (normalized.min > bounds.min || normalized.max < bounds.max);

  const histogramBars = useMemo(() => {
    if (externalHistogramBars?.length) {
      return externalHistogramBars;
    }

    const bars = Array.from({ length: bins }, () => 0);

    if (!histogramValues.length) {
      return bars;
    }

    histogramValues.forEach((entry) => {
      const safeValue = Number.isFinite(entry) ? entry : bounds.min;
      const index =
        bounds.max === bounds.min
          ? 0
          : Math.min(
              bins - 1,
              Math.floor(((safeValue - bounds.min) / (bounds.max - bounds.min)) * bins)
            );

      bars[index] = (bars[index] ?? 0) + 1;
    });

    return bars;
  }, [bins, bounds.max, bounds.min, externalHistogramBars, histogramValues]);

  const histogramPeak = Math.max(...histogramBars, 1);

  const setMin = (nextMin: number) => {
    const clampedMin = Math.min(Math.max(nextMin, bounds.min), normalized.max);
    onChange({ min: clampedMin, max: normalized.max });
  };

  const setMax = (nextMax: number) => {
    const clampedMax = Math.max(Math.min(nextMax, bounds.max), normalized.min);
    onChange({ min: normalized.min, max: clampedMax });
  };

  const reset = () => {
    onChange({ min: bounds.min, max: bounds.max });
  };

  return (
    <div
      className={`${sharedStyles.root} ${isActive ? sharedStyles.rootActive : ''} ${isOpen ? sharedStyles.rootOpen : ''}`}
      ref={filterRef}
    >
      <span>{label}</span>
      <button
        type="button"
        className={sharedStyles.trigger}
        aria-label={`Filter by ${label.toLowerCase()}`}
        ref={triggerRef}
        onClick={() => {
          setIsOpen((prev) => !prev);
        }}
      >
        <img src={filterIcon} alt="Filter" className={sharedStyles.icon} />
      </button>

      {isOpen
        ? createPortal(
        <div
          className={sharedStyles.popover}
          ref={popoverRef}
          style={{
            top: `${popoverPosition.top}px`,
            left: `${popoverPosition.left}px`,
            width: `${popoverPosition.width}px`,
          }}
        >
          <button
            type="button"
            className={`${sharedStyles.reset} ${isActive ? sharedStyles.resetActive : ''}`}
            onClick={reset}
          >
            Reset All
          </button>

          <div className={styles.chart}>
            {histogramBars.map((count, index) => {
              const height = count > 0 ? Math.max(14, Math.round((count / histogramPeak) * 50)) : 0;
              return <span key={index} className={styles.bar} style={{ height }} />;
            })}
          </div>

          <div className={styles.sliderWrapper}>
            <div className={styles.sliderTrack}>
              <span
                className={styles.sliderActive}
                style={{
                  left: `${minPercent}%`,
                  width: `${Math.max(0, maxPercent - minPercent)}%`,
                }}
              />
            </div>
            <input
              type="range"
              min={bounds.min}
              max={bounds.max}
              step={1}
              value={normalized.min}
              className={styles.range}
              onChange={(event) => {
                setMin(Number(event.target.value));
              }}
            />
            <input
              type="range"
              min={bounds.min}
              max={bounds.max}
              step={1}
              value={normalized.max}
              className={styles.range}
              onChange={(event) => {
                setMax(Number(event.target.value));
              }}
            />
          </div>

          <div className={styles.inputsRow}>
            <label className={styles.inputCard}>
              <span className={styles.inputLabel}>Min</span>
              <input
                type="number"
                min={bounds.min}
                max={normalized.max}
                value={normalized.min}
                className={styles.input}
                onChange={(event) => {
                  setMin(Number(event.target.value));
                }}
              />
            </label>
            <label className={styles.inputCard}>
              <span className={styles.inputLabel}>Max</span>
              <input
                type="number"
                min={normalized.min}
                max={bounds.max}
                value={normalized.max}
                className={styles.input}
                onChange={(event) => {
                  setMax(Number(event.target.value));
                }}
              />
            </label>
          </div>
        </div>,
          document.body
        )
        : null}
    </div>
  );
};
